import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { registerBannerClick, resolveStorefrontImageUrl } from '../api/client';
import { getTranslation as t, isRTL } from '../utils/translations';
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight, ShieldCheck, Zap, Crown } from 'lucide-react';
import Hls from 'hls.js';

const Hero = ({ banner, storeSettings }) => {
    const defaultLuxurySlides = [
        {
            titleAr: 'منصة الاستيراد والتسوق الفاخر',
            titleEn: 'Premier Global Sourcing & Luxury Platform',
            subtitleAr: 'استورد مباشرة من كبرى المصانع والماركات المعتمدة مع ضمان الفحص والشحن السريع',
            subtitleEn: 'Direct factory sourcing, authenticated suppliers and guaranteed express worldwide logistics.',
            tagAr: '✦ جودة مضمونة 100%',
            tagEn: '✦ 100% Guaranteed Quality',
            ctaTextAr: 'تسوق الآن',
            ctaTextEn: 'Explore Collection',
            ctaLink: '/category/all',
            secondaryTextAr: 'طلب استيراد خاص',
            secondaryTextEn: 'Bespoke Sourcing',
            secondaryLink: '/sourcing',
            gradient: 'linear-gradient(135deg, #050505 0%, #111111 50%, #1a1a1a 100%)',
            accentColor: '#FE2C55',
            icon: Sparkles
        },
        {
            titleAr: 'عروض حصرية وخصومات استثنائية',
            titleEn: 'Exclusive Deals & Wholesale Pricing',
            subtitleAr: 'أقوى الصفقات والخصومات على أحدث المنتجات الفاخرة والإلكترونيات الذكية',
            subtitleEn: 'Unlock exceptional discounts on trending tech, luxury lifestyle and premium gear.',
            tagAr: '🔥 عروض محدودة',
            tagEn: '🔥 Limited-Time Deals',
            ctaTextAr: 'اقتناء العروض ⚡',
            ctaTextEn: 'Shop Flash Deals ⚡',
            ctaLink: '/category/all',
            secondaryTextAr: 'الأكثر مبيعاً',
            secondaryTextEn: 'Best Sellers',
            secondaryLink: '/category/all',
            gradient: 'linear-gradient(135deg, #0a0a0a 0%, #141414 40%, #1c0609 100%)',
            accentColor: '#FE2C55',
            icon: Zap
        },
        {
            titleAr: 'خدمات الكونسيرج و VIP للمستوردين',
            titleEn: 'VIP Concierge & Commercial Trade',
            subtitleAr: 'حلول متكاملة لرجال الأعمال والشركات لتأمين عقود التوريد وإدارة الشحنات الكبرى',
            subtitleEn: 'Dedicated procurement advisors, bulk order negotiation and white-glove trade support.',
            tagAr: '👑 خدمات VIP الخاصة',
            tagEn: '👑 VIP Concierge Service',
            ctaTextAr: 'تواصل مع مستشارك',
            ctaTextEn: 'Contact VIP Concierge',
            ctaLink: '/vip',
            secondaryTextAr: 'سجل الفخامة',
            secondaryTextEn: 'The Magazine',
            secondaryLink: '/magazine',
            gradient: 'linear-gradient(135deg, #000000 0%, #0d0d0d 50%, #161616 100%)',
            accentColor: '#FE2C55',
            icon: Crown
        }
    ];

    const normalizedHeroType = String(storeSettings?.heroType || 'slideshow').toLowerCase() === 'gradient'
        ? 'animated'
        : String(storeSettings?.heroType || 'slideshow').toLowerCase();
    
    const customBgUrl = storeSettings?.homepageBanner || banner?.imageUrl || banner?.image || '';
    
    const heroType = (normalizedHeroType === 'image' && !customBgUrl)
        ? 'slideshow'
        : (normalizedHeroType === 'video' && (storeSettings?.homepageVideoEnabled === false || !storeSettings?.homepageVideoUrl)
            ? 'slideshow'
            : normalizedHeroType);

    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    
    const videoRef = useRef(null);
    const hlsRef = useRef(null);

    // Auto-advance slideshow when not hovered
    useEffect(() => {
        if (heroType === 'slideshow' && !isHovered) {
            const interval = setInterval(() => {
                setCurrentSlideIndex(prev => (prev + 1) % defaultLuxurySlides.length);
            }, 6000);
            return () => clearInterval(interval);
        }
    }, [heroType, isHovered]);

    // HLS Video Support
    useEffect(() => {
        if (heroType === 'video' && videoRef.current) {
            const videoSrc = storeSettings?.homepageVideoUrl || '';
            const isHlsStream = typeof videoSrc === 'string' && videoSrc.includes('.m3u8');
            
            if (isHlsStream) {
                if (Hls.isSupported()) {
                    if (hlsRef.current) {
                        hlsRef.current.destroy();
                    }
                    const hls = new Hls({ enableWorker: false });
                    hls.loadSource(videoSrc);
                    hls.attachMedia(videoRef.current);
                    hlsRef.current = hls;
                } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
                    videoRef.current.src = videoSrc;
                }
            } else {
                videoRef.current.src = videoSrc;
            }
        }
        
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [heroType, storeSettings?.homepageVideoUrl]);

    const activeSlide = defaultLuxurySlides[currentSlideIndex] || defaultLuxurySlides[0];
    const SlideIcon = activeSlide.icon || Sparkles;

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentSlideIndex(prev => (prev === 0 ? defaultLuxurySlides.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentSlideIndex(prev => (prev + 1) % defaultLuxurySlides.length);
    };

    return (
        <section 
            className={`hero hero-${heroType} luxury-hero-revamp`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ position: 'relative', overflow: 'hidden', height: '100%', minHeight: '420px', borderRadius: '16px' }}
        >
            {/* Background Layer */}
            <div className="hero-bg-layer" style={{ position: 'absolute', inset: 0 }}>
                {heroType === 'image' && (
                    <>
                        <div
                            className="hero-bg-image"
                            style={{
                                backgroundImage: customBgUrl
                                    ? `url(${resolveStorefrontImageUrl(customBgUrl)})`
                                    : activeSlide.gradient,
                                position: 'absolute',
                                inset: 0,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }}
                        />
                        <div
                            className="hero-contrast-overlay"
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: isRTL()
                                    ? 'linear-gradient(to left, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0.62) 55%, rgba(15, 23, 42, 0.35) 100%)'
                                    : 'linear-gradient(to right, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0.62) 55%, rgba(15, 23, 42, 0.35) 100%)',
                                zIndex: 2
                            }}
                        />
                    </>
                )}

                {heroType === 'video' && (
                    <video
                        ref={videoRef}
                        className="hero-bg-video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}

                {heroType === 'slideshow' && defaultLuxurySlides.map((slide, i) => (
                    <div
                        key={i}
                        className={`hero-bg-image hero-slide ${i === currentSlideIndex ? 'active' : ''}`}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: slide.gradient,
                            opacity: i === currentSlideIndex ? 1 : 0,
                            transition: 'opacity 0.8s ease-in-out',
                            zIndex: 1
                        }}
                    >
                        {/* Decorative subtle ambient lights */}
                        <div style={{
                            position: 'absolute',
                            top: '-20%',
                            right: isRTL() ? 'auto' : '-10%',
                            left: isRTL() ? '-10%' : 'auto',
                            width: '320px',
                            height: '320px',
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${slide.accentColor}25, transparent 70%)`,
                            pointerEvents: 'none'
                        }} />
                    </div>
                ))}
            </div>

            {/* Slide Content Overlay */}
            <div 
                className="luxury-hero-content-wrap"
                style={{
                    position: 'relative',
                    zIndex: 10,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: isRTL() ? '32px 48px 32px 32px' : '32px 32px 32px 48px',
                    textAlign: isRTL() ? 'right' : 'left',
                    color: '#ffffff',
                    boxSizing: 'border-box'
                }}
            >
                {/* Badge Tag */}
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(8px)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: activeSlide.accentColor,
                    width: 'fit-content',
                    marginBottom: '12px'
                }}>
                    <SlideIcon size={14} />
                    <span>{isRTL() ? activeSlide.tagAr : activeSlide.tagEn}</span>
                </div>

                {/* Main Headline */}
                <h1 style={{
                    fontSize: '26px',
                    fontWeight: '900',
                    lineHeight: '1.25',
                    margin: '0 0 10px 0',
                    color: '#ffffff',
                    letterSpacing: '-0.02em',
                    maxWidth: '480px'
                }}>
                    {isRTL() ? activeSlide.titleAr : activeSlide.titleEn}
                </h1>

                {/* Subtitle / Description */}
                <p style={{
                    fontSize: '13.5px',
                    color: '#cbd5e1',
                    lineHeight: '1.55',
                    margin: '0 0 20px 0',
                    maxWidth: '440px',
                    fontWeight: '400'
                }}>
                    {isRTL() ? activeSlide.subtitleAr : activeSlide.subtitleEn}
                </p>

                {/* Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <Link
                        to={activeSlide.ctaLink}
                        className="btn-primary"
                        onClick={() => registerBannerClick(banner?.id)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: '#ffffff',
                            color: '#111827',
                            padding: '10px 22px',
                            borderRadius: '12px',
                            fontSize: '13.5px',
                            fontWeight: '700',
                            textDecoration: 'none',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <span>{isRTL() ? activeSlide.ctaTextAr : activeSlide.ctaTextEn}</span>
                        <ArrowRight size={15} style={{ transform: isRTL() ? 'rotate(180deg)' : 'none' }} />
                    </Link>

                    {activeSlide.secondaryLink && (
                        <Link
                            to={activeSlide.secondaryLink}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                color: '#ffffff',
                                padding: '10px 18px',
                                borderRadius: '12px',
                                fontSize: '13px',
                                fontWeight: '600',
                                textDecoration: 'none',
                                backdropFilter: 'blur(8px)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <span>{isRTL() ? activeSlide.secondaryTextAr : activeSlide.secondaryTextEn}</span>
                        </Link>
                    )}
                </div>
            </div>

            {/* Navigation Arrows (Prev / Next) */}
            {heroType === 'slideshow' && (
                <>
                    <button
                        onClick={isRTL() ? handleNext : handlePrev}
                        aria-label="Previous Slide"
                        style={{
                            position: 'absolute',
                            [isRTL() ? 'right' : 'left']: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: 'rgba(0, 0, 0, 0.75)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 20,
                            backdropFilter: 'blur(6px)',
                            transition: 'all 0.2s ease',
                            opacity: isHovered ? 1 : 0
                        }}
                    >
                        {isRTL() ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                    </button>

                    <button
                        onClick={isRTL() ? handlePrev : handleNext}
                        aria-label="Next Slide"
                        style={{
                            position: 'absolute',
                            [isRTL() ? 'left' : 'right']: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: 'rgba(0, 0, 0, 0.75)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 20,
                            backdropFilter: 'blur(6px)',
                            transition: 'all 0.2s ease',
                            opacity: isHovered ? 1 : 0
                        }}
                    >
                        {isRTL() ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                    </button>
                </>
            )}

            {/* Slide Pagination Indicators */}
            {heroType === 'slideshow' && defaultLuxurySlides.length > 1 && (
                <div style={{
                    position: 'absolute',
                    bottom: '16px',
                    [isRTL() ? 'left' : 'right']: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    zIndex: 20
                }}>
                    {defaultLuxurySlides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentSlideIndex(i)}
                            aria-label={`Slide ${i + 1}`}
                            style={{
                                width: i === currentSlideIndex ? '24px' : '7px',
                                height: '7px',
                                borderRadius: '4px',
                                background: i === currentSlideIndex ? '#ffffff' : 'rgba(255, 255, 255, 0.35)',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

export default Hero;
